// useAnnaChat — React hook for streaming chat with Anna (Claude Haiku)
//
// Manages chat messages, streaming state, tool results, and Bloom updates.
// Builds the system prompt from teaching form + session context via promptBuilder.

import { useState, useCallback, useRef } from 'react';
import { streamAnnaChat } from '@services/annaService';
import { buildHaikuSystemPrompt, type PromptOptions } from '@services/promptBuilder';
import { ANNA_TOOLS } from '@services/toolDefinitions';
import type { Tool } from '@anthropic-ai/sdk/resources/messages';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolCalls?: Array<{ id: string; name: string; input: Record<string, unknown> }>;
}

export interface ToolResult {
  id: string;
  toolName: string;
  input: Record<string, unknown>;
  timestamp: Date;
}

export interface BloomUpdate {
  concept: string;
  level: string;
}

interface UseAnnaChatOptions {
  teachingForm: 'introduction' | 'dialog' | 'exam';
  topicTitle: string;
  subtopicTitle: string;
  conceptMapState?: string;
  bloomStatus?: string;
  sessionFraming?: { level: string; pace: string };
  examFormat?: string;
  /** Override the tool set — defaults to ANNA_TOOLS if omitted */
  tools?: Tool[];
}

interface UseAnnaChatReturn {
  messages: ChatMessage[];
  toolResults: ToolResult[];
  bloomUpdates: BloomUpdate[];
  isStreaming: boolean;
  error: string | null;
  sendMessage: (text: string) => Promise<void>;
  clearError: () => void;
}

function generateId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Date.now().toString(36) + Math.random().toString(36).slice(2);
}

export function useAnnaChat(options: UseAnnaChatOptions): UseAnnaChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [toolResults, setToolResults] = useState<ToolResult[]>([]);
  const [bloomUpdates, setBloomUpdates] = useState<BloomUpdate[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for stable closure access during streaming
  const abortRef = useRef<AbortController | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const clearError = useCallback(() => setError(null), []);

  const sendMessage = useCallback(async (text: string) => {
    if (isStreaming) return;

    setError(null);
    setIsStreaming(true);

    // Build user message
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    // Build assistant placeholder
    const assistantId = generateId();
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      toolCalls: [],
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);

    // Build system prompt from current context (use ref for stable access)
    const currentOptions = optionsRef.current;
    const promptOptions: PromptOptions = {
      teachingForm: currentOptions.teachingForm,
      topicTitle: currentOptions.topicTitle,
      subtopicTitle: currentOptions.subtopicTitle,
      conceptMapState: currentOptions.conceptMapState,
      bloomStatus: currentOptions.bloomStatus,
      sessionFraming: currentOptions.sessionFraming,
      examFormat: currentOptions.examFormat,
    };
    const system = buildHaikuSystemPrompt(promptOptions);

    // Build message history for the API (use ref for current messages)
    const apiMessages = [...messagesRef.current, userMessage].map((m) => ({
      role: m.role,
      content: m.content,
    }));

    // Create abort controller for this stream
    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      // Use caller-supplied tools (e.g. EXAM_TOOLS) or fall back to full set
      const activeTools = (optionsRef.current.tools ?? ANNA_TOOLS) as unknown[];
      for await (const event of streamAnnaChat(
        { system, messages: apiMessages, tools: activeTools },
        abortController.signal,
      )) {
        switch (event.type) {
          case 'text_delta': {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: m.content + (event.text || '') }
                  : m,
              ),
            );
            break;
          }

          case 'tool_use': {
            const toolCall = {
              id: event.toolId || generateId(),
              name: event.toolName || 'unknown',
              input: event.toolInput || {},
            };

            // Add to assistant message's tool calls
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, toolCalls: [...(m.toolCalls || []), toolCall] }
                  : m,
              ),
            );

            // Add to tool results
            setToolResults((prev) => [
              ...prev,
              {
                id: toolCall.id,
                toolName: toolCall.name,
                input: toolCall.input,
                timestamp: new Date(),
              },
            ]);

            // Track Bloom level updates specifically
            if (toolCall.name === 'update_bloom_level') {
              const input = toolCall.input as { concept?: string; level?: string };
              if (input.concept && input.level) {
                setBloomUpdates((prev) => [
                  ...prev,
                  { concept: input.concept!, level: input.level! },
                ]);
              }
            }
            break;
          }

          case 'error': {
            setError(event.error || 'Unknown streaming error');
            break;
          }

          case 'message_stop': {
            // Stream completed normally
            break;
          }
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User-initiated cancellation — not an error
      } else {
        setError(err instanceof Error ? err.message : 'Unexpected error');
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [isStreaming]); // messagesRef + optionsRef provide stable access without deps

  return {
    messages,
    toolResults,
    bloomUpdates,
    isStreaming,
    error,
    sendMessage,
    clearError,
  };
}
